<template>
  <iBaseModal
    class="i-subscription-bundle-modal"
    use-light-close-icon
    :visible="props.visible"
    :fully-customisable="true"
    :show-close-icon="false"
    max-width="970px"
    @click-on-close="emit('close')"
  >
    <div class="inner">
      <div class="left">
        <img
          v-if="!isMdOrLarger"
          class="best-value"
          :src="ImageBestValue"
        />
        <div class="only">
          <img
            class="sparkle"
            :src="IconSparkle"
          />
          {{ GlobalFunctions.setToLastDateTimeOfMonth().toLocaleString("default", { month: "long" }) }} Only Offer
        </div>
      </div>
      <div class="right">
        <img
          v-if="isMdOrLarger"
          class="best-value"
          :src="ImageBestValue"
        />
        <div class="top">
          <div class="header">3 Months of Coaching<br /><span class="gradient">1 Month Free</span></div>
          <div class="partner">
            <div class="with">
              With
              <img
                class="klarna"
                :src="JobtestKlarna"
              />
            </div>
            Exclusive Partnership
          </div>
          <div class="keys">
            <div
              v-for="(key, i) in KEY_DATA"
              :key="i"
              class="key"
            >
              <img
                v-if="key.image"
                class="key-icon"
                :src="key.image"
              />
              {{ key.text }}
            </div>
          </div>
        </div>
        <div class="middle">
          <div
            v-for="(detail, i) in DETAILS"
            :key="i"
            class="detail"
          >
            <img
              class="dot top"
              :src="IconDot"
            />
            {{ detail }}
            <img
              class="dot"
              :class="{ last: i + 1 === DETAILS.length }"
              :src="IconDot"
            />
          </div>
        </div>
        <div class="bottom">
          <div
            v-if="countdown && !countdown.isFinished"
            class="expiry"
          >
            <div class="offer">
              <img
                class="clock"
                :src="IconClock"
              />
              OFFER EXPIRES IN
            </div>
            <div class="timer">
              <div class="time">{{ countdown.daysPadded }} <span class="faded">DAYS</span></div>
              <div class="time">{{ countdown.hoursPadded }} <span class="faded">HRS</span></div>
              <div class="time">{{ countdown.minutesPadded }} <span class="faded">MIN</span></div>
              <div class="time">{{ countdown.secondsPadded }} <span class="faded">SEC</span></div>
            </div>
          </div>
          <iBaseButton
            v-bind="MAIN_BUTTON_STYLE"
            text="Claim Your Free Month Now"
            @clicked="$emit('go-to-bundle-page')"
          />
          <div
            class="thanks"
            @click="$emit('close')"
          >
            No, thanks
          </div>
        </div>
      </div>
    </div>
  </iBaseModal>
</template>

<script setup lang="ts">
import { bluePurpleGradient, white } from "@/styles/colors"
import { breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import type { CountdownState } from "@/composables/useCountdown"
import { GlobalFunctions } from "@/helpers/globalFunctions"

import iBaseModal from "@/components/base/BaseModal.vue"
import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import IconGiftBlack from "@/assets/icons/icon-gift-black.svg"
import IconGiftWhite from "@/assets/icons/icon-gift-white-tiny.svg"
import IconClock from "@/assets/icons/icon-clock-black.svg"
import IconDot from "@/assets/icons/icon-circle-dot-grey.svg"
import IconSparkle from "@/assets/icons/icon-black-sparkle.svg"
import JobtestKlarna from "@/assets/images/image-jobtest-klarna.svg"
import ImageBestValue from "@/assets/images/cb-best-value.svg"

// EMITS
const emit = defineEmits(["close", "go-to-bundle-page"])

// PROPS
const props = defineProps({
  visible: { type: Boolean, required: true },
  countdown: { type: Object as () => CountdownState, default: null }
})

// COMPOSABLES
const isMdOrLarger = useIsScreenSize(breakpointMd)

// CONSTANTS
const KEY_DATA = [{ image: IconGiftBlack, text: "Save 25%" }, { text: "0% Interest" }, { text: "No Fees" }]
const DETAILS = [
  "Grab our Klarna coaching bundle.",
  "Pay over time like your current subscription.",
  "Get a full month of coaching free!"
]
const MAIN_BUTTON_STYLE = {
  preIcon: IconGiftWhite,
  backgroundImage: bluePurpleGradient,
  borderRadius: "16px",
  fontWeight: "500",
  textColor: white,
  width: "100%",
  padding: "10px 32px",
  size: "medium",
  isHoverable: true
}
</script>

<style scoped lang="scss">
.i-subscription-bundle-modal {
  .inner {
    @include flex(column, start, stretch);

    border-radius: 16px;
    overflow: clip;
    text-align: center;

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, start, stretch);
    }

    .best-value {
      position: absolute;
      top: 0;
      right: 0;
      width: 130px;
    }

    .left {
      @include flex(column, end, center);

      height: 40lvh;
      background-image: url("@/assets/images/cb-modal-image.svg");
      background-size: cover;
      background-repeat: no-repeat;
      background-position: 0% 30%;
      padding-bottom: 15px;

      @media screen and (min-width: $breakpoint-md) {
        width: 38%;
        height: unset;
        background-position: unset;
      }

      .only {
        @include flex(row, center, center, $gap: 10px);
        @include border-radius-md;
        @include text-sm;

        background-color: #fffc;
        padding: 10px 30px;
        font-weight: 500;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-md;

          padding: 10px;
          width: 85%;
        }

        .sparkle {
          width: 20px;
        }
      }
    }

    .right {
      @include flex(column, start, center, $gap: 20px);

      position: relative;
      padding: 15px;
      background-color: $black50;

      @media screen and (min-width: $breakpoint-sm) {
        padding: 30px 20px;
      }

      @media screen and (min-width: $breakpoint-md) {
        width: 62%;
        gap: 25px;
      }

      .top {
        @include flex(column, start, center, $gap: 8px);

        position: relative;

        .header {
          @include display-sm;

          @media screen and (min-width: $breakpoint-sm) {
            @include display-md;
          }

          @media screen and (min-width: $breakpoint-md) {
            @include display-lg;
          }

          .gradient {
            background-image: linear-gradient(
              270deg,
              $riptide400 21.03%,
              $azure-radiance400 25%,
              $astronaut600 50%,
              $purple-heart600 75%,
              $amethyst500 100%
            );
            color: transparent;
            background-clip: text;
            font-weight: 700;
          }
        }

        .partner {
          @include flex(column, start, center, $gap: 6px);
          @include text-xs;

          color: $pickle-bluewood950;
          font-weight: 600;

          @media screen and (min-width: $breakpoint-md) {
            gap: 10px;
          }

          .with {
            @include flex(column, start, center, $gap: 1px);

            .klarna {
              height: 25px;

              @media screen and (min-width: $breakpoint-sm) {
                height: 30px;
              }

              @media screen and (min-width: $breakpoint-md) {
                height: 35px;
              }
            }
          }
        }

        .keys {
          @include flex(row, center, center, wrap, $gap: 10px);

          margin-top: 4px;

          @media screen and (min-width: $breakpoint-sm) {
            @include flex(row, center, center, $gap: 10px);
          }

          .key {
            @include flex(row, center, center, $gap: 6px);
            @include text-sm;
            @include border-radius-xxl;

            color: $pickle-bluewood950;
            padding: 3px 8px;
            background-color: $white;
            font-weight: 500;
            border: 1px solid $black300;

            @media screen and (min-width: $breakpoint-sm) {
              @include text-md;

              padding: 6px 12px;
            }

            .key-icon {
              width: 18px;
            }
          }
        }
      }

      .middle {
        @include flex(column, start, center);
        @include text-sm;

        color: $pickle-bluewood950;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-md;

          gap: 10px;
        }

        @media screen and (min-width: $breakpoint-md) {
          gap: 15px;
        }

        .detail {
          @include flex(column, start, center);

          @media screen and (min-width: $breakpoint-sm) {
            @include flex(row, center, center, $gap: 8px);
          }

          .dot {
            width: 8px;

            &.top {
              display: none;

              @media screen and (min-width: $breakpoint-sm) {
                display: block;
              }
            }

            &.last {
              display: none;

              @media screen and (min-width: $breakpoint-sm) {
                display: block;
              }
            }
          }
        }
      }

      .bottom {
        @include flex(column, start, center, $gap: 25px);
        @include text-sm;

        color: $pickle-bluewood950;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-md;
        }

        .expiry {
          @include flex(column, start, center, $gap: 10px);

          .offer {
            @include flex(row, center, stretch, $gap: 8px);

            .clock {
              width: 20px;
            }
          }

          .timer {
            @include flex(row, center, stretch, $gap: 10px);

            .time {
              @include flex(column, center, center);
              @include border-radius-md;
              @include text-sm;

              font-weight: 900;
              height: 60px;
              width: 54px;
              color: white;
              background-color: $comet950;

              @media screen and (min-width: $breakpoint-sm) {
                @include text-md;
              }

              .faded {
                @include text-xxs;

                font-weight: 300;
                color: $black300;

                @media screen and (min-width: $breakpoint-sm) {
                  @include text-xs;
                }
              }
            }
          }
        }

        .thanks {
          text-decoration: underline;
          cursor: pointer;

          &:hover {
            color: $pickle-bluewood700;
          }
        }
      }
    }
  }
}
</style>
